import React from "react";

/**
 * Converts various case styles to Title Case
 * Handles camelCase, snake_case, SCREAMING_CASE, kebab-case, etc.
 */
export const toTitleCase = (str: string): string => {
  // Handle empty strings
  if (!str) return "";

  // Replace special characters with spaces
  let result = str.replace(/[-_]/g, " ");

  // Handle camelCase by adding spaces before capital letters
  result = result.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Split into words, capitalize first letter of each word, and join
  return result
    .split(" ")
    .map((word) => {
      if (!word) return "";
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export interface ProcessedData {
  listKey: string | null;
  listData: any[] | null;
  otherData: Record<string, any>;
}

/**
 * Attempts to parse JSON strings in the data
 */
export const parseJsonStrings = <T extends Record<string, any>>(data: T): T => {
  const processedData = { ...data };

  for (const key in processedData) {
    if (typeof processedData[key] === "string") {
      try {
        const parsed = JSON.parse(processedData[key] as string);
        if (parsed && typeof parsed === "object") {
          processedData[key] = parsed;
        }
      } catch (e) {
        // Not valid JSON, keep as string
      }
    }
  }

  return processedData;
};

/**
 * Processes data to extract list data and other data from an object
 * Finds the first array property to display as a main list, and collects
 * the rest of the properties as other data
 */
export const processObjectData = (data: Record<string, any>): ProcessedData => {
  const parsedData = parseJsonStrings(data);

  let listKey: string | null = null;
  let listData: any[] | null = null;
  const otherData: Record<string, any> = {};

  // First pass: Check for direct arrays of objects
  for (const key in parsedData) {
    const value = parsedData[key];

    if (
      Array.isArray(value) &&
      value.length > 0 &&
      (typeof value[0] === "object" || typeof value[0] === "string")
    ) {
      if (!listKey) {
        listKey = key;
        listData = value;
      } else {
        otherData[key] = value;
      }
    } else {
      otherData[key] = value;
    }
  }

  // Second pass: If no array found directly, check for nested arrays
  if (!listKey) {
    for (const key in parsedData) {
      const value = parsedData[key];

      if (value && typeof value === "object" && !Array.isArray(value)) {
        // Check if this object has an array property we can use
        for (const nestedKey in value as Record<string, any>) {
          const nestedValue = (value as Record<string, any>)[nestedKey];

          if (
            Array.isArray(nestedValue) &&
            nestedValue.length > 0 &&
            (typeof nestedValue[0] === "object" ||
              typeof nestedValue[0] === "string")
          ) {
            // Found a nested array of objects
            if (!listKey) {
              listKey = nestedKey;
              listData = nestedValue;

              const valueObj = value as Record<string, any>;
              const newObj: Record<string, any> = {};

              for (const k in valueObj) {
                if (k !== nestedKey) {
                  newObj[k] = valueObj[k];
                }
              }

              otherData[key] = newObj;
            }
          }
        }
      }
    }
  }

  return { listKey, listData, otherData };
};

/**
 * Safely formats a value for display, handling objects and complex values
 */
export const formatValueForDisplay = (value: any): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "object") {
    // For simple objects, attempt to stringify them
    if (!Array.isArray(value) && Object.keys(value).length <= 3) {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return null; // Skip if we can't stringify
      }
    } else {
      return null; // Skip complex objects
    }
  }

  return String(value);
};

/**
 * Creates a key-value display object from any flat object
 * Useful for displaying simple object properties in a details section
 */
export const createKeyValuePairs = (
  obj: Record<string, any>
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const key in obj) {
    const value = obj[key];
    if (typeof value !== "object" || value === null) {
      result[key] = String(value);
    }
  }

  return result;
};

/**
 * Groups an array of objects by a specific key
 * Returns an object where keys are the values of the specified key
 * and values are arrays of objects
 */
export const groupArrayByKey = <T extends Record<string, any>>(
  array: T[],
  key: keyof T
): Record<string, T[]> => {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
};

/**
 * Truncates a string to a maximum length and adds an ellipsis if needed
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (!str || str.length <= maxLength) return str;
  return str.substring(0, maxLength) + "...";
};
